import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import { Eye, EyeOff, Gift, MailCheck, Phone, RefreshCw } from "lucide-react";
import { siteConfig } from "../lib/siteConfig";

const SIGNUP_DRAFT_KEY = "fea_signup_draft_v2";
const SIGNUP_PENDING_KEY = "fea_signup_pending_email_v2";
const SIGNUP_STORAGE_TTL_MS = 6 * 60 * 60 * 1000;

function readSignupStorage(key) {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(key) || "null");
    if (!parsed?.savedAt || Date.now() - Number(parsed.savedAt) > SIGNUP_STORAGE_TTL_MS) {
      window.sessionStorage.removeItem(key);
      return null;
    }
    return parsed;
  } catch {
    window.sessionStorage.removeItem(key);
    return null;
  }
}

function writeSignupStorage(key, value) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ ...value, savedAt: Date.now() }));
  } catch {
    // Ignore private-mode/session storage failures; signup still works in memory.
  }
}

function clearSignupStorage() {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(SIGNUP_DRAFT_KEY);
  window.sessionStorage.removeItem(SIGNUP_PENDING_KEY);
}

export default function Signup() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [search] = useSearchParams();
  const [form, setForm] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    date_of_birth: "",
    full_name: "",
    email: "",
    phone: "",
    password: "",
    referral_code: "",
  });
  const [showReferral, setShowReferral] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [verification, setVerification] = useState(null);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { first_name, middle_name, last_name, date_of_birth, email, phone, referral_code } = form;
  const fullNameDraft = composeFullLegalName({ first_name, middle_name, last_name });
  const generatedUsername = makeSignupUsername(fullNameDraft, email);

  useEffect(() => {
    const pending = readSignupStorage(SIGNUP_PENDING_KEY);
    if (pending?.email) {
      setVerification({
        email: pending.email,
        message: pending.message || "Verification email sent. Check your Gmail to finish signup.",
        codeRequired: true,
      });
      return;
    }
    const draft = readSignupStorage(SIGNUP_DRAFT_KEY);
    if (draft?.form) {
      const legacyName = splitFullLegalName(draft.form.full_name || "");
      setForm((current) => ({
        ...current,
        first_name: draft.form.first_name || legacyName.first_name || "",
        middle_name: draft.form.middle_name || legacyName.middle_name || "",
        last_name: draft.form.last_name || legacyName.last_name || "",
        date_of_birth: draft.form.date_of_birth || "",
        full_name: draft.form.full_name || "",
        email: draft.form.email || "",
        phone: draft.form.phone || "",
        referral_code: draft.form.referral_code || "",
      }));
      setShowReferral(Boolean(draft.form.referral_code || draft.showReferral));
    }
  }, []);

  useEffect(() => {
    if (verification) return;
    const safeForm = {
      first_name,
      middle_name,
      last_name,
      date_of_birth,
      full_name: fullNameDraft,
      email,
      phone,
      referral_code,
    };
    if (safeForm.full_name || safeForm.date_of_birth || safeForm.email || safeForm.phone || safeForm.referral_code) {
      writeSignupStorage(SIGNUP_DRAFT_KEY, { form: safeForm, showReferral });
    }
  }, [first_name, middle_name, last_name, date_of_birth, fullNameDraft, email, phone, referral_code, showReferral, verification]);

  // Pre-fill referral code from URL (?ref=ABC123)
  useEffect(() => {
    const ref = search.get("ref");
    if (ref) {
      setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
      setShowReferral(true);
    }
  }, [search]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const payload = { ...form };
      const firstName = cleanNamePart(payload.first_name);
      const middleName = cleanNamePart(payload.middle_name);
      const lastName = cleanNamePart(payload.last_name);
      if (!isValidNamePart(firstName) || !isValidNamePart(lastName)) {
        setErr("Enter your first and last name.");
        return;
      }
      const dateOfBirth = dobInputToIso(payload.date_of_birth);
      if (!dateOfBirth) {
        setErr("Enter your date of birth.");
        return;
      }
      payload.first_name = firstName;
      payload.middle_name = middleName;
      payload.last_name = lastName;
      payload.date_of_birth = dateOfBirth;
      const legalName = composeFullLegalName(payload);
      payload.full_name = legalName;
      if (!payload.referral_code) delete payload.referral_code;
      const phoneDigits = String(payload.phone || "").replace(/\D/g, "").slice(0, 10);
      if (phoneDigits) payload.phone = `+1${phoneDigits}`;
      else delete payload.phone;
      const { data } = await api.post("/auth/register", payload);
      if (data?.email_verification_required && !data?.token) {
        const pendingVerification = {
          email: data.email || payload.email,
          message: data.message || "Verification email sent. Check your Gmail to finish signup.",
          codeRequired: Boolean(data.code_required),
        };
        setVerification(pendingVerification);
        writeSignupStorage(SIGNUP_PENDING_KEY, pendingVerification);
        return;
      }
      clearSignupStorage();
      login(data.token, data.user);
      nav("/dashboard");
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setLoading(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    const cleanCode = verificationCode.replace(/\D/g, "");
    if (cleanCode.length !== 6) {
      setErr("Enter the 6-digit Gmail code.");
      return;
    }
    setErr("");
    setResendMessage("");
    setVerifyLoading(true);
    try {
      const { data } = await api.post("/auth/email/verify", {
        email: verification?.email || form.email,
        code: cleanCode,
      });
      clearSignupStorage();
      login(data.token, data.user);
      nav("/dashboard", { replace: true });
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setVerifyLoading(false);
    }
  };

  const resendVerification = async () => {
    const targetEmail = verification?.email || form.email;
    if (!targetEmail) return;
    setErr("");
    setResendMessage("");
    setResendLoading(true);
    try {
      const { data } = await api.post("/auth/email/resend", { email: targetEmail });
      setResendMessage(data?.message || "Verification email sent. Check your Gmail.");
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setResendLoading(false);
    }
  };

  const restartSignup = () => {
    clearSignupStorage();
    setVerification(null);
    setVerificationCode("");
    setErr("");
    setResendMessage("");
  };

  if (verification) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-950">
        <Navbar />
        <div className="max-w-md mx-auto px-6 pt-16 pb-24">
          <div className="rounded-2xl border border-[#2F6FA5]/25 bg-white p-6 shadow-xl shadow-slate-200/80">
            <div className="w-14 h-14 rounded-full bg-[#2F6FA5]/15 border border-[#2F6FA5]/40 flex items-center justify-center mb-5">
              <MailCheck className="w-7 h-7 text-[#6EA8D8]" />
            </div>
            <p className="text-xs uppercase tracking-[0.22em] text-[#2F6FA5] mb-2">Verify email</p>
            <h1 className="font-display text-3xl font-black tracking-tighter mb-3">Enter Gmail code</h1>
            <p className="text-slate-600 leading-relaxed">
              {verification.message} Your account will be created only after this code is verified.
            </p>
            <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600 break-all">
              {verification.email}
            </div>
            <form onSubmit={verifyCode} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Gmail code</label>
                <input
                  data-testid="signup-email-code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                  maxLength={6}
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  className="w-full px-4 py-3 rounded-lg bg-white border border-[#2F6FA5]/30 text-slate-950 focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20 text-center text-2xl font-black tracking-[0.5em]"
                />
              </div>
              <button
                data-testid="signup-verify-code"
                type="submit"
                disabled={verifyLoading}
                className="w-full py-3 bg-[#2F6FA5] text-white font-bold rounded-full hover:-translate-y-0.5 hover:bg-[#4E91C8] transition-all disabled:opacity-50"
              >
                {verifyLoading ? "Creating account..." : "Verify code & create account"}
              </button>
            </form>
            {resendMessage && (
              <div className="mt-4 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                {resendMessage}
              </div>
            )}
            {err && (
              <div data-testid="signup-error" className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
                {err}
              </div>
            )}
            <button
              type="button"
              onClick={resendVerification}
              disabled={resendLoading}
              className="mt-4 w-full py-3 border border-[#2F6FA5]/40 text-[#6EA8D8] font-bold rounded-full hover:bg-[#2F6FA5]/10 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${resendLoading ? "animate-spin" : ""}`} />
              {resendLoading ? "Sending..." : "Resend Gmail code"}
            </button>
            <button
              type="button"
              onClick={restartSignup}
              className="mt-3 w-full py-2 text-sm font-semibold text-slate-500 hover:text-[#2F6FA5]"
            >
              Use a different email
            </button>
            <p className="mt-5 text-sm text-slate-600 text-center">
              Already verified? <Link to="/login" className="text-[#2F6FA5] hover:underline">Sign in</Link>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <Navbar />
      <div className="max-w-md mx-auto px-6 pt-16 pb-24">
        <h1 className="font-display text-4xl font-black tracking-tighter mb-2">Create account</h1>
        <p className="text-slate-600 mb-10">Free to play · No credit card · 18+ only</p>
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Legal name</label>
            <div className="grid gap-3 sm:grid-cols-2">
              <input
                data-testid="signup-first-name"
                required
                minLength={2}
                maxLength={50}
                value={form.first_name}
                onChange={(e)=>setForm({...form, first_name: e.target.value})}
                placeholder="First name"
                autoComplete="given-name"
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-950 focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20"
              />
              <input
                data-testid="signup-middle-name"
                maxLength={50}
                value={form.middle_name}
                onChange={(e)=>setForm({...form, middle_name: e.target.value})}
                placeholder="Middle name"
                autoComplete="additional-name"
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-950 focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20"
              />
              <input
                data-testid="signup-last-name"
                required
                minLength={2}
                maxLength={50}
                value={form.last_name}
                onChange={(e)=>setForm({...form, last_name: e.target.value})}
                placeholder="Last name"
                autoComplete="family-name"
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-950 focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20"
              />
              <input
                data-testid="signup-date-of-birth"
                type="text"
                inputMode="numeric"
                required
                maxLength={10}
                value={form.date_of_birth}
                onChange={(e)=>setForm({...form, date_of_birth: formatDobInput(e.target.value)})}
                autoComplete="bday"
                placeholder="MM/DD/YYYY"
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 text-slate-950 [color-scheme:light] focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20"
              />
            </div>
            <p className="mt-2 text-xs font-semibold text-slate-500">First name, last name, and date of birth are required.</p>
            {generatedUsername && (
              <p className="mt-2 text-xs font-semibold text-slate-500">
                Username auto: <span className="text-[#2F6FA5]">{generatedUsername}</span>
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Email</label>
            <input data-testid="signup-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})}
              className="w-full px-4 py-3 rounded-lg bg-white border border-slate-200 text-slate-950 focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-2">
              <Phone className="w-3.5 h-3.5 text-[#2F6FA5]" /> Phone number <span className="text-slate-400 normal-case tracking-normal">(optional)</span>
            </label>
            <div className="flex">
              <div className="flex h-[50px] w-16 shrink-0 items-center justify-center rounded-l-lg border border-r-0 border-slate-200 bg-slate-100 font-bold text-[#2F6FA5]">
                +1
              </div>
              <input
                data-testid="signup-phone"
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={(e)=>setForm({...form, phone: e.target.value.replace(/\D/g, "").slice(0, 10)})}
                placeholder="(412) 516-9125"
                className="h-[50px] w-full min-w-0 rounded-r-lg border border-slate-200 bg-white px-4 text-slate-950 [color-scheme:light] focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Password</label>
            <div className="relative">
              <input
                data-testid="signup-password"
                type={showPassword ? "text" : "password"}
                required
                minLength={6}
                value={form.password}
                onChange={(e)=>setForm({...form, password: e.target.value})}
                className="w-full rounded-lg border border-slate-200 bg-white px-4 py-3 pr-12 text-slate-950 [color-scheme:light] focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20"
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                data-testid="signup-toggle-password"
                className="absolute inset-y-0 right-0 grid w-12 place-items-center rounded-r-lg text-slate-500 hover:text-[#2F6FA5]"
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Referral code — collapsed by default */}
          {!showReferral ? (
            <button
              type="button"
              onClick={() => setShowReferral(true)}
              data-testid="signup-show-referral"
              className="flex items-center gap-2 text-sm text-[#2F6FA5] hover:text-[#6EA8D8]"
            >
              <Gift className="w-4 h-4" /> Have a referral code? Add it
            </button>
          ) : (
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-2">
                <Gift className="w-3.5 h-3.5 text-[#2F6FA5]" /> Referral code <span className="text-slate-400 normal-case tracking-normal">(optional)</span>
              </label>
              <input
                data-testid="signup-referral-code"
                value={form.referral_code}
                onChange={(e)=>setForm({...form, referral_code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12)})}
                placeholder="E.G. SLOTS123"
                maxLength={12}
                className="w-full px-4 py-3 rounded-lg bg-white border border-[#2F6FA5]/30 text-slate-950 focus:border-[#2F6FA5] focus:outline-none focus:ring-2 focus:ring-[#2F6FA5]/20 uppercase tracking-[0.3em] font-mono"
              />
            </div>
          )}

          {err && <div data-testid="signup-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">{err}</div>}
          <button data-testid="signup-submit" type="submit" disabled={loading}
            className="w-full py-3 bg-[#2F6FA5] text-white font-bold rounded-full hover:-translate-y-0.5 hover:bg-[#4E91C8] transition-all disabled:opacity-50">
            {loading ? "Sending code..." : "Create account"}
          </button>
        </form>
        <p className="mt-8 text-sm text-slate-600 text-center">
          Have an account? <Link to="/login" data-testid="signup-login-link" className="text-[#2F6FA5] hover:underline">Sign in</Link>
        </p>
        <p className="mt-4 text-xs text-slate-400 text-center">By signing up you agree to {siteConfig.name} terms. 18+ only.</p>
      </div>
    </div>
  );
}

function makeSignupUsername(fullName, email = "") {
  const emailLocal = String(email || "").split("@")[0] || "";
  const source = String(fullName || emailLocal || "").replace(/[^A-Za-z0-9]+/g, "").toLowerCase();
  if (source.length >= 3) return source.slice(0, 24);
  const fallback = emailLocal.replace(/[^A-Za-z0-9]+/g, "").toLowerCase();
  return fallback.length >= 3 ? fallback.slice(0, 24) : "";
}

function cleanFullLegalName(value) {
  return String(value || "").trim().replace(/\s+/g, " ");
}

function cleanNamePart(value) {
  return cleanFullLegalName(value).replace(/[^\p{L}' -]/gu, "").trim();
}

function isValidNamePart(value) {
  return cleanNamePart(value).replace(/[^\p{L}]/gu, "").length >= 2;
}

function composeFullLegalName(value) {
  return [value.first_name, value.middle_name, value.last_name].map(cleanNamePart).filter(Boolean).join(" ");
}

function splitFullLegalName(value) {
  const parts = cleanFullLegalName(value).split(" ").filter(Boolean);
  if (parts.length <= 1) return { first_name: parts[0] || "", middle_name: "", last_name: "" };
  return {
    first_name: parts[0],
    middle_name: parts.slice(1, -1).join(" "),
    last_name: parts[parts.length - 1],
  };
}

function formatDobInput(value) {
  const raw = String(value || "").trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return `${iso[2]}/${iso[3]}/${iso[1]}`;
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function dobInputToIso(value) {
  const raw = String(value || "").trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (iso) return validDob(iso[1], iso[2], iso[3]) ? raw : "";
  const match = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) return "";
  const [, month, day, year] = match;
  const mm = month.padStart(2, "0");
  const dd = day.padStart(2, "0");
  return validDob(year, mm, dd) ? `${year}-${mm}-${dd}` : "";
}

function validDob(year, month, day) {
  const y = Number(year);
  const m = Number(month);
  const d = Number(day);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d) || y < 1900) return false;
  const parsed = new Date(y, m - 1, d);
  if (parsed.getFullYear() !== y || parsed.getMonth() !== m - 1 || parsed.getDate() !== d) return false;
  const today = new Date();
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return parsed <= todayDate;
}
